import { useEffect, useRef } from "react"

import { cn } from "@/lib/utils"

/**
 * The conversation so far.
 *
 * Three bot voices, deliberately distinct: a fixed question is solid white, a
 * follow-up is lighter (plan Q16) so the member can see it is an aside rather
 * than one of the seven, and an acknowledgment — the line the bot says back
 * before moving on — carries no card at all, because it is the interviewer
 * murmuring, not asking.
 */
export function ChatTranscript({ turns, thinking = false }) {
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [turns, thinking])

  return (
    <div className="flex flex-col gap-4">
      {turns.map((turn, index) => (
        <div
          key={`${turn.role}-${index}`}
          className={cn("flex", turn.role === "member" ? "justify-end" : "justify-start")}
        >
          <div
            className={cn(
              "max-w-[85%] text-sm leading-relaxed",
              !turn.isAside && "rounded-2xl px-4 py-3",
              turn.role === "member" && "rounded-br-sm bg-kf-red text-white",
              turn.role === "bot" &&
                !turn.isFollowup &&
                !turn.isAside &&
                "rounded-bl-sm bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200",
              turn.role === "bot" &&
                turn.isFollowup &&
                "rounded-bl-sm bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200/60",
              turn.isAside && "px-1 py-1 italic text-zinc-500"
            )}
          >
            {turn.text}
          </div>
        </div>
      ))}

      {thinking && (
        <div className="flex justify-start">
          <div
            className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-white px-4 py-3.5 shadow-sm ring-1 ring-zinc-200"
            aria-label="Kingdom Factor is typing"
            role="status"
          >
            {[ 0, 150, 300 ].map((delay) => (
              <span
                key={delay}
                className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </div>
        </div>
      )}

      <div ref={endRef} />
    </div>
  )
}
